import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, errorMessage } from "@/lib/api";
import { AField, BiField, Btn, PageHead } from "@/admin/AdminLayout";

export default function Settings() {
  const qc = useQueryClient();
  const [s, setS] = useState(null);
  const [pw, setPw] = useState({ current_password: "", new_password: "" });
  const { data } = useQuery({ queryKey: ["admin-settings"], queryFn: async () => (await api.get("/admin/settings")).data });
  const { data: email } = useQuery({ queryKey: ["email-status"], queryFn: async () => (await api.get("/admin/leads/email-status")).data });
  const testEmail = async () => {
    try {
      const r = await api.post("/admin/leads/test-email");
      toast.success(`Test email sent to ${r.data.sent_to}`);
    } catch (e) { toast.error(errorMessage(e)); }
  };
  useEffect(() => { if (data) setS(data); }, [data]);
  if (!s) return null;
  const set = (k) => (e) => setS((x) => ({ ...x, [k]: e.target.value }));
  const save = async () => {
    try {
      await api.put("/admin/settings", s);
      toast.success("Settings saved");
      qc.invalidateQueries({ queryKey: ["settings"] }); qc.invalidateQueries({ queryKey: ["admin-settings"] });
    } catch (e) { toast.error(errorMessage(e)); }
  };
  const changePw = async (e) => {
    e.preventDefault();
    try {
      await api.post("/auth/change-password", pw);
      toast.success("Password changed");
      setPw({ current_password: "", new_password: "" });
    } catch (err) { toast.error(errorMessage(err)); }
  };
  return (
    <div data-testid="admin-settings">
      <PageHead title="Site Settings"><Btn onClick={save} data-testid="settings-save">SAVE</Btn></PageHead>
      <div className="space-y-5">
        <div className="admin-card grid gap-4 p-5 sm:grid-cols-3">
          <AField label="WhatsApp (display)"><input className="field" value={s.whatsapp} onChange={set("whatsapp")} data-testid="settings-whatsapp" /></AField>
          <AField label="WhatsApp link"><input className="field" value={s.whatsapp_link} onChange={set("whatsapp_link")} /></AField>
          <AField label="Contact email"><input className="field" value={s.email} onChange={set("email")} data-testid="settings-email" /></AField>
        </div>
        <div className="admin-card space-y-5 p-5">
          <p className="mono text-[0.58rem] text-[#8b95a5]">SEO defaults</p>
          <BiField label="Site title" value={s.seo_title} onChange={(v) => setS((x) => ({ ...x, seo_title: v }))} />
          <BiField label="Site description" value={s.seo_description} onChange={(v) => setS((x) => ({ ...x, seo_description: v }))} textarea />
          <AField label="Open Graph image URL"><input className="field" value={s.og_image || ""} onChange={set("og_image")} /></AField>
        </div>
        <div className="admin-card p-5" data-testid="email-status-card">
          <p className="mono mb-3 text-[0.58rem] text-[#8b95a5]">Email notifications (Resend)</p>
          {email ? (
            <div className="space-y-2 text-sm">
              <p className="flex items-center gap-2"><span className="status-dot" style={{ background: email.configured ? "#10b981" : "#e2c89d" }} />{email.configured ? "Active" : "Not configured yet"} <span className="text-[#8b95a5]">— sender {email.sender} → {email.recipient}</span></p>
              {!email.configured && <p className="text-xs text-[#8b95a5]">Add <code>RESEND_API_KEY</code> to backend/.env (and set <code>SENDER_EMAIL</code> to website@palissieremotorgroup.com once the domain is verified in Resend), then restart the backend. Leads are always stored here regardless.</p>}
              <Btn variant="ghost" onClick={testEmail} disabled={!email.configured} data-testid="email-test">SEND TEST EMAIL</Btn>
            </div>
          ) : null}
        </div>
        <div className="admin-card space-y-5 p-5" data-testid="reply-templates-editor">
          <div className="flex items-center justify-between">
            <p className="mono text-[0.58rem] text-[#8b95a5]">Reply templates (WhatsApp / email) — placeholders: {"{name} {vehicle} {signature}"}</p>
            <Btn variant="ghost" onClick={() => setS((x) => ({ ...x, reply_templates: [...(x.reply_templates || []), { id: `t${Date.now()}`, name: { fr: "", en: "" }, subject: { fr: "", en: "" }, body: { fr: "", en: "" } }] }))} data-testid="template-add">ADD TEMPLATE</Btn>
          </div>
          <AField label="Signature"><textarea className="field" rows={3} value={s.reply_signature || ""} onChange={set("reply_signature")} data-testid="reply-signature" /></AField>
          {(s.reply_templates || []).map((t, i) => {
            const upd = (patch) => setS((x) => ({ ...x, reply_templates: x.reply_templates.map((y, j) => (j === i ? { ...y, ...patch } : y)) }));
            return (
              <div key={t.id} className="space-y-3 border-t border-white/10 pt-4" data-testid={`template-editor-${t.id}`}>
                <div className="flex items-center justify-between">
                  <span className="mono text-[0.55rem] text-[#8b95a5]">Template {i + 1}</span>
                  <button onClick={() => setS((x) => ({ ...x, reply_templates: x.reply_templates.filter((_, j) => j !== i) }))} className="text-xs text-red-300 hover:text-red-200" data-testid={`template-remove-${t.id}`}>Remove</button>
                </div>
                <BiField label={`Name ${i + 1}`} value={t.name} onChange={(v) => upd({ name: v })} />
                <BiField label={`Email subject ${i + 1}`} value={t.subject} onChange={(v) => upd({ subject: v })} />
                <BiField label={`Message ${i + 1}`} value={t.body} onChange={(v) => upd({ body: v })} textarea rows={5} />
              </div>
            );
          })}
        </div>
        <form onSubmit={changePw} className="admin-card grid gap-4 p-5 sm:grid-cols-3" data-testid="change-password-form">
          <p className="mono text-[0.58rem] text-[#8b95a5] sm:col-span-3">Change admin password</p>
          <AField label="Current password"><input className="field" type="password" value={pw.current_password} onChange={(e) => setPw({ ...pw, current_password: e.target.value })} required data-testid="pw-current" /></AField>
          <AField label="New password (8+ chars)"><input className="field" type="password" minLength={8} value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} required data-testid="pw-new" /></AField>
          <div className="flex items-end"><Btn type="submit" variant="ghost" data-testid="pw-submit">UPDATE PASSWORD</Btn></div>
        </form>
        <p className="text-xs text-[#8b95a5]">Notifications for contact forms, sourcing requests, sell submissions and vehicle inquiries are sent to {s.email}.</p>
      </div>
    </div>
  );
}
